import i18n.StringKey
import i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLElement
import kotlin.js.Date

/**
 * Banner displayed at the top of the dashboard showing subscription/trial status.
 *
 * States:
 * - trialing: "Free trial — X days remaining" with Subscribe CTA
 * - active: nothing shown (paid user)
 * - expired/past_due/canceled/none: read-only overlay with Subscribe CTA
 */
class SubscriptionBanner(
    private val onSubscribeClick: () -> Unit = {}
) {
    private var container: HTMLElement? = null
    private val apiService = ApiService()

    fun render(): HTMLElement {
        val wrapper = document.createElement("div") as HTMLElement
        wrapper.id = "subscription-banner-wrapper"
        wrapper.style.cssText = "position: relative; z-index: 50; flex-shrink: 0;"
        container = wrapper
        // Load status async
        GlobalScope.launch {
            try {
                val status = apiService.getSubscriptionStatus()
                updateBanner(status)
            } catch (e: Exception) {
                console.log("Failed to load subscription status: ${e.message}")
                // Don't show banner on error — fail open
            }
        }
        return wrapper
    }

    private fun updateBanner(status: SubscriptionStatusData) {
        val wrapper = container ?: return
        // Keep the shared read-only cache in sync. Web write CTAs read from
        // this cache via ReadOnlyGate.block() to surface the bottom toast.
        ReadOnlyGate.updateStatus(status.status)

        when (status.status) {
            "active" -> {
                // Paid user — no banner
                wrapper.style.display = "none"
            }
            "trialing" -> {
                val daysLeft = calculateDaysLeft(status.trialEndsAt)
                val urgencyColor = when {
                    daysLeft <= 3 -> "#ff4444"
                    daysLeft <= 7 -> "#ffaa00"
                    else -> "#00aaff"
                }
                val urgencyBg = when {
                    daysLeft <= 3 -> "rgba(255, 68, 68, 0.1)"
                    daysLeft <= 7 -> "rgba(255, 170, 0, 0.1)"
                    else -> "rgba(0, 170, 255, 0.1)"
                }
                val daysText = if (daysLeft == 1) {
                    StringKey.BANNER_DAY_REMAINING.tr()
                } else {
                    StringKey.BANNER_DAYS_REMAINING.tr(daysLeft.toString())
                }
                wrapper.innerHTML = """
                    <div style="
                        background: $urgencyBg;
                        border: 1px solid ${urgencyColor}40;
                        border-radius: 12px;
                        padding: 12px 20px;
                        margin: 16px 32px 0;
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        flex-wrap: wrap;
                        gap: 12px;
                    ">
                        <div style="display: flex; align-items: center; gap: 10px;">
                            <span style="font-size: 18px;">⏳</span>
                            <span style="color: #ffffff; font-size: 14px; font-weight: 500;">
                                ${StringKey.BANNER_FREE_TRIAL.tr()} — <strong style="color: $urgencyColor;">$daysText</strong>
                            </span>
                        </div>
                        <button id="trial-subscribe-btn" style="
                            background: linear-gradient(135deg, #6c63ff, #4ecdc4);
                            color: white;
                            border: none;
                            border-radius: 8px;
                            padding: 8px 20px;
                            font-size: 14px;
                            font-weight: 600;
                            cursor: pointer;
                            transition: opacity 0.2s;
                        ">${StringKey.BANNER_SUBSCRIBE_NOW.tr()}</button>
                    </div>
                """.trimIndent()

                wrapper.querySelector("#trial-subscribe-btn")?.addEventListener("click", {
                    onSubscribeClick()
                })
            }
            else -> {
                // expired, past_due, canceled, or no subscription
                wrapper.innerHTML = """
                    <div style="
                        background: rgba(255, 68, 68, 0.12);
                        border: 1px solid rgba(255, 68, 68, 0.3);
                        border-radius: 12px;
                        padding: 16px 20px;
                        margin: 16px 32px 0;
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        flex-wrap: wrap;
                        gap: 12px;
                    ">
                        <div style="display: flex; align-items: center; gap: 10px;">
                            <span style="font-size: 18px;">🔒</span>
                            <span style="color: #ffffff; font-size: 14px; font-weight: 500;">
                                ${StringKey.BANNER_TRIAL_ENDED.tr()} <strong style="color: #ff4444;">${StringKey.BANNER_READ_ONLY.tr()}</strong>
                            </span>
                        </div>
                        <button id="expired-subscribe-btn" style="
                            background: linear-gradient(135deg, #6c63ff, #4ecdc4);
                            color: white;
                            border: none;
                            border-radius: 8px;
                            padding: 10px 24px;
                            font-size: 14px;
                            font-weight: 600;
                            cursor: pointer;
                            transition: opacity 0.2s;
                            animation: pulse-glow 2s ease-in-out infinite;
                        ">${StringKey.BANNER_SUBSCRIBE_UNLOCK.tr()}</button>
                    </div>
                    <style>
                        @keyframes pulse-glow {
                            0%, 100% { box-shadow: 0 0 8px rgba(108, 99, 255, 0.3); }
                            50% { box-shadow: 0 0 20px rgba(108, 99, 255, 0.6); }
                        }
                    </style>
                """.trimIndent()

                wrapper.querySelector("#expired-subscribe-btn")?.addEventListener("click", {
                    onSubscribeClick()
                })
            }
        }
    }

    private fun calculateDaysLeft(trialEndsAt: String?): Int {
        if (trialEndsAt == null) return 0
        return try {
            // Normalize to ISO 8601 UTC: ensure "T" separator and "Z" suffix.
            // Server stores trial_ends_at in UTC; without "Z", JS Date() treats it as local time.
            val withT = if (!trialEndsAt.contains("T")) trialEndsAt.replace(" ", "T") else trialEndsAt
            val normalized = if (!withT.endsWith("Z")) "${withT}Z" else withT
            val endDate = Date(normalized)
            val now = Date()
            val diffMs = endDate.getTime() - now.getTime()
            if (diffMs <= 0) return 0
            // Use ceiling so any remaining time shows at least 1 day
            val days = kotlin.math.ceil(diffMs / (1000.0 * 60 * 60 * 24)).toInt()
            days
        } catch (e: Exception) {
            0
        }
    }
}
